class ZermielPrelaunchCatalog {
  constructor(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-catalog-dialog]');
    this.openButton = root.querySelector('[data-catalog-open]');
    this.closeButton = root.querySelector('[data-catalog-close]');
    this.contactDialog = root.querySelector('[data-contact-dialog]');
    this.contactOpenButtons = [...root.querySelectorAll('[data-contact-open]')];
    this.contactCloseButton = root.querySelector('[data-contact-close]');
    this.contactPanel = root.querySelector('[data-contact-panel]');
    this.privacyPanel = root.querySelector('[data-privacy-panel]');
    this.cookiePanel = root.querySelector('[data-cookie-panel]');
    this.privacyOpenButton = root.querySelector('[data-privacy-open]');
    this.privacyBackButton = root.querySelector('[data-privacy-back]');
    this.cookiePrivacyOpenButton = root.querySelector('[data-cookie-privacy-open]');
    this.contactDialogEyebrow = root.querySelector('[data-contact-dialog-eyebrow]');
    this.privacyDialogEyebrow = root.querySelector('[data-privacy-dialog-eyebrow]');
    this.cookieDialogEyebrow = root.querySelector('[data-cookie-dialog-eyebrow]');
    this.contactDialogTitle = root.querySelector('[data-contact-dialog-title]');
    this.privacyDialogTitle = root.querySelector('[data-privacy-dialog-title]');
    this.cookieDialogTitle = root.querySelector('[data-cookie-dialog-title]');
    this.desktopNavigation = root.querySelector('[data-catalog-navigation]');
    this.mobileNavigation = root.querySelector('[data-catalog-select]');
    this.heading = root.querySelector('[data-catalog-heading]');
    this.grid = root.querySelector('[data-catalog-grid]');
    this.data = this.readData();
    this.selectedCategory = null;
    this.lastFocusedElement = null;

    if (!this.dialog || !this.openButton || !this.data) return;

    this.categories = [...this.data.categories].sort(this.sortByOrder);
    this.items = [...this.data.items].sort(this.sortByOrder);
    this.categoryByKey = new Map(this.categories.map((category) => [category.key, category]));

    this.bindEvents();
    this.renderNavigation();
    this.renderContent();
    this.root.dataset.catalogReady = 'true';

    if (this.root.querySelector('[data-contact-auto-open]')) {
      requestAnimationFrame(() => this.openContact());
    }
  }

  sortByOrder(a, b) {
    return Number(a.order || 0) - Number(b.order || 0) || a.label?.localeCompare(b.label, 'ro');
  }

  readData() {
    try {
      return JSON.parse(this.root.querySelector('[data-catalog-data]')?.textContent || '');
    } catch (error) {
      console.error('Zermiel presentation catalogue data is invalid.', error);
      return null;
    }
  }

  bindEvents() {
    this.openButton.addEventListener('click', (event) => this.open(event.currentTarget));
    this.closeButton.addEventListener('click', () => this.close());
    this.bindDialog(this.dialog, () => this.close());
    this.contactOpenButtons.forEach((button) => {
      button.addEventListener('click', (event) => this.openContact(event.currentTarget));
    });
    if (this.contactDialog && this.contactCloseButton) {
      this.contactCloseButton.addEventListener('click', () => this.closeContact());
      this.bindDialog(this.contactDialog, () => this.closeContact());
    }
    this.privacyOpenButton?.addEventListener('click', () => this.showPrivacyPanel({ allowBack: true }));
    this.privacyBackButton?.addEventListener('click', () => this.showContactPanel({ focus: true }));
    this.cookiePrivacyOpenButton?.addEventListener('click', () => this.showPrivacyPanel({ allowBack: false }));
    this.mobileNavigation.addEventListener('change', (event) => {
      this.selectCategory(event.target.value || null);
    });
  }

  bindDialog(dialog, close) {
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
  }

  openDialog(dialog, opener, closeButton) {
    this.lastFocusedElement = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('zermiel-modal-open');
    closeButton.focus();
  }

  closeDialog(dialog) {
    if (dialog?.open) dialog.close();
    if (!this.root.querySelector('dialog[open]')) {
      document.documentElement.classList.remove('zermiel-modal-open');
    }
    this.lastFocusedElement?.focus();
  }

  open(opener) {
    this.openDialog(this.dialog, opener, this.closeButton);
  }

  close() {
    this.closeDialog(this.dialog);
  }

  openContact(opener) {
    if (!this.contactDialog || !this.contactCloseButton) return;
    const requestedView = opener?.dataset.contactView;
    if (requestedView === 'privacy') {
      this.showPrivacyPanel({ focus: false, allowBack: false });
    } else if (requestedView === 'cookies') {
      this.showCookiePanel({ focus: false });
    } else {
      this.showContactPanel();
    }
    this.openDialog(this.contactDialog, opener, this.contactCloseButton);
    if (requestedView === 'privacy') {
      this.privacyPanel?.focus({ preventScroll: true });
    } else if (requestedView === 'cookies') {
      this.cookiePanel?.focus({ preventScroll: true });
    }
  }

  closeContact() {
    this.closeDialog(this.contactDialog);
    this.showContactPanel();
  }

  showContactPanel({ focus = false } = {}) {
    this.contactDialog?.classList.remove('is-privacy-view');
    if (this.contactPanel) this.contactPanel.hidden = false;
    if (this.privacyPanel) this.privacyPanel.hidden = true;
    if (this.cookiePanel) this.cookiePanel.hidden = true;
    if (this.contactDialogEyebrow) this.contactDialogEyebrow.hidden = false;
    if (this.privacyDialogEyebrow) this.privacyDialogEyebrow.hidden = true;
    if (this.cookieDialogEyebrow) this.cookieDialogEyebrow.hidden = true;
    if (this.contactDialogTitle) this.contactDialogTitle.hidden = false;
    if (this.privacyDialogTitle) this.privacyDialogTitle.hidden = true;
    if (this.cookieDialogTitle) this.cookieDialogTitle.hidden = true;
    if (focus) this.privacyOpenButton?.focus({ preventScroll: true });
  }

  showPrivacyPanel({ focus = true, allowBack = true } = {}) {
    if (!this.privacyPanel) return;
    this.contactDialog?.classList.add('is-privacy-view');
    if (this.contactPanel) this.contactPanel.hidden = true;
    this.privacyPanel.hidden = false;
    if (this.cookiePanel) this.cookiePanel.hidden = true;
    if (this.privacyBackButton) this.privacyBackButton.hidden = !allowBack;
    if (this.contactDialogEyebrow) this.contactDialogEyebrow.hidden = true;
    if (this.privacyDialogEyebrow) this.privacyDialogEyebrow.hidden = false;
    if (this.cookieDialogEyebrow) this.cookieDialogEyebrow.hidden = true;
    if (this.contactDialogTitle) this.contactDialogTitle.hidden = true;
    if (this.privacyDialogTitle) this.privacyDialogTitle.hidden = false;
    if (this.cookieDialogTitle) this.cookieDialogTitle.hidden = true;
    if (focus) this.privacyPanel.focus({ preventScroll: true });
  }

  showCookiePanel({ focus = true } = {}) {
    if (!this.cookiePanel) return;
    this.contactDialog?.classList.add('is-privacy-view');
    if (this.contactPanel) this.contactPanel.hidden = true;
    if (this.privacyPanel) this.privacyPanel.hidden = true;
    this.cookiePanel.hidden = false;
    if (this.contactDialogEyebrow) this.contactDialogEyebrow.hidden = true;
    if (this.privacyDialogEyebrow) this.privacyDialogEyebrow.hidden = true;
    if (this.cookieDialogEyebrow) this.cookieDialogEyebrow.hidden = false;
    if (this.contactDialogTitle) this.contactDialogTitle.hidden = true;
    if (this.privacyDialogTitle) this.privacyDialogTitle.hidden = true;
    if (this.cookieDialogTitle) this.cookieDialogTitle.hidden = false;
    if (focus) this.cookiePanel.focus({ preventScroll: true });
  }

  childrenOf(parentKey) {
    return this.categories.filter((category) => (category.parent || null) === parentKey);
  }

  rootCategories() {
    return this.childrenOf(null);
  }

  selectCategory(key) {
    this.selectedCategory = key && this.categoryByKey.has(key) ? key : null;
    this.renderNavigation();
    this.renderContent();
    this.grid.focus({ preventScroll: true });
  }

  renderNavigation() {
    const allButton = this.navigationButton(null, 'Toate categoriile');
    const navigation = document.createDocumentFragment();
    navigation.append(allButton);

    this.rootCategories().forEach((category) => {
      const group = document.createElement('div');
      group.className = 'zermiel-catalog__navigation-group';
      group.append(this.navigationButton(category.key, category.label));

      const children = this.childrenOf(category.key);
      if (children.length) {
        const childList = document.createElement('div');
        childList.className = 'zermiel-catalog__navigation-children';
        children.forEach((child) => childList.append(this.navigationButton(child.key, child.label, true)));
        group.append(childList);
      }
      navigation.append(group);
    });

    this.desktopNavigation.replaceChildren(navigation);
    this.renderMobileNavigation();
  }

  navigationButton(key, label, child = false) {
    const button = document.createElement('button');
    const selected = this.selectedCategory === key;
    const selectedCategory = this.categoryByKey.get(this.selectedCategory);
    const ancestorActive = key && selectedCategory?.parent === key;

    button.type = 'button';
    button.className = `zermiel-catalog__navigation-button${child ? ' is-child' : ''}`;
    button.textContent = label;
    button.dataset.category = key || '';
    button.setAttribute('aria-current', selected ? 'page' : 'false');
    if (ancestorActive) button.classList.add('is-ancestor');
    button.addEventListener('click', () => this.selectCategory(key));
    return button;
  }

  renderMobileNavigation() {
    const options = [new Option('Toate categoriile', '')];
    this.rootCategories().forEach((category) => {
      options.push(new Option(category.label, category.key));
      this.childrenOf(category.key).forEach((child) => {
        options.push(new Option(`— ${child.label}`, child.key));
      });
    });
    this.mobileNavigation.replaceChildren(...options);
    this.mobileNavigation.value = this.selectedCategory || '';
  }

  renderContent() {
    if (!this.selectedCategory) {
      this.heading.textContent = 'Colecții de prezentare';
      this.renderCards(this.rootCategories(), 'category');
      return;
    }

    const category = this.categoryByKey.get(this.selectedCategory);
    const children = this.childrenOf(category.key);
    this.heading.textContent = category.label;

    if (children.length) {
      this.renderCards(children, 'category');
      return;
    }

    const items = this.items.filter((item) => item.category === category.key);
    this.renderCards(items, 'item');
  }

  renderCards(records, type) {
    if (!records.length) {
      const empty = document.createElement('p');
      empty.className = 'zermiel-catalog__empty';
      empty.textContent = 'Această selecție va fi completată în curând.';
      this.grid.replaceChildren(empty);
      return;
    }

    const fragment = document.createDocumentFragment();
    records.forEach((record) => {
      fragment.append(type === 'category' ? this.categoryCard(record) : this.itemCard(record));
    });
    this.grid.replaceChildren(fragment);
  }

  categoryCard(category) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'zermiel-catalog-card zermiel-catalog-card--category';
    card.addEventListener('click', () => this.selectCategory(category.key));

    if (category.image) {
      card.append(this.image(category.image, `Selecție din categoria ${category.label}`, true));
    }

    const content = document.createElement('span');
    content.className = 'zermiel-catalog-card__content';
    const title = document.createElement('span');
    title.className = 'zermiel-catalog-card__title';
    title.textContent = category.label;
    content.append(title);
    if (category.introduction) {
      const introduction = document.createElement('span');
      introduction.className = 'zermiel-catalog-card__description';
      introduction.textContent = category.introduction;
      content.append(introduction);
    }
    card.append(content);
    return card;
  }

  itemCard(item) {
    const card = document.createElement('article');
    card.className = 'zermiel-catalog-card zermiel-catalog-card--item';

    const gallery = document.createElement('div');
    gallery.className = 'zermiel-catalog-card__gallery';
    gallery.dataset.galleryIndex = '0';

    const images = item.images.map((source, index) => {
      const image = this.image(
        source,
        index === 0 ? item.name : `${item.name}, imaginea ${index + 1}`,
        index === 0
      );
      image.hidden = index !== 0;
      image.dataset.galleryImage = '';
      return image;
    });
    gallery.append(...images);

    if (images.length > 1) {
      const loading = document.createElement('span');
      loading.className = 'zermiel-catalog-card__gallery-loading';
      loading.dataset.galleryLoading = '';
      loading.hidden = true;
      loading.setAttribute('role', 'status');
      loading.setAttribute('aria-live', 'polite');
      loading.innerHTML =
        '<span class="zermiel-catalog-card__spinner" aria-hidden="true"></span><span>Se încarcă imaginea…</span>';
      gallery.append(
        this.galleryButton('previous', 'Imaginea anterioară', '‹'),
        this.galleryButton('next', 'Imaginea următoare', '›'),
        loading
      );
      const status = document.createElement('span');
      status.className = 'zermiel-catalog-card__gallery-status';
      status.dataset.galleryStatus = '';
      status.setAttribute('aria-live', 'polite');
      status.textContent = `1 / ${images.length}`;
      gallery.append(status);
      this.preloadImage(images[1]);
      const preloadPrevious = () => this.preloadImage(images.at(-1));
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preloadPrevious, { timeout: 1800 });
      } else {
        window.setTimeout(preloadPrevious, 800);
      }
    }

    const content = document.createElement('div');
    content.className = 'zermiel-catalog-card__content';
    const title = document.createElement('h3');
    title.className = 'zermiel-catalog-card__title';
    title.textContent = item.name;
    const description = document.createElement('p');
    description.className = 'zermiel-catalog-card__description';
    description.textContent = item.description;
    content.append(title, description);
    card.append(gallery, content);
    return card;
  }

  galleryButton(direction, label, symbol) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `zermiel-catalog-card__gallery-button is-${direction}`;
    button.setAttribute('aria-label', label);
    button.textContent = symbol;
    button.addEventListener('click', (event) => this.changeGalleryImage(event.currentTarget, direction));
    return button;
  }

  async changeGalleryImage(button, direction) {
    const gallery = button.closest('.zermiel-catalog-card__gallery');
    if (gallery.dataset.galleryLoading === 'true') return;
    const images = [...gallery.querySelectorAll('[data-gallery-image]')];
    const offset = direction === 'next' ? 1 : -1;
    const current = Number(gallery.dataset.galleryIndex);
    const next = (current + offset + images.length) % images.length;
    const nextImage = images[next];
    const controls = [...gallery.querySelectorAll('.zermiel-catalog-card__gallery-button')];
    const loading = gallery.querySelector('[data-gallery-loading]');

    this.preloadImage(nextImage);
    if (!nextImage.complete || nextImage.naturalWidth === 0) {
      gallery.dataset.galleryLoading = 'true';
      gallery.classList.add('is-loading');
      loading.hidden = false;
      controls.forEach((control) => {
        control.disabled = true;
      });
      const loaded = await this.waitForImage(nextImage);
      gallery.dataset.galleryLoading = 'false';
      gallery.classList.remove('is-loading');
      loading.hidden = true;
      controls.forEach((control) => {
        control.disabled = false;
      });
      if (!loaded) return;
    }

    images[current].hidden = true;
    nextImage.hidden = false;
    gallery.dataset.galleryIndex = String(next);
    gallery.querySelector('[data-gallery-status]').textContent = `${next + 1} / ${images.length}`;
    this.preloadImage(images[(next + offset + images.length) % images.length]);
  }

  preloadImage(image) {
    if (!image || image.getAttribute('src')) return;
    image.loading = 'eager';
    image.src = image.dataset.src;
  }

  waitForImage(image) {
    if (image.complete) return Promise.resolve(image.naturalWidth > 0);
    return new Promise((resolve) => {
      const complete = () => resolve(image.naturalWidth > 0);
      image.addEventListener('load', complete, { once: true });
      image.addEventListener('error', complete, { once: true });
    });
  }

  image(source, alt, loadImmediately = false) {
    const image = document.createElement('img');
    image.className = 'zermiel-catalog-card__image';
    image.dataset.src = source;
    if (loadImmediately) image.src = source;
    image.alt = alt;
    image.loading = loadImmediately ? 'eager' : 'lazy';
    image.decoding = 'async';
    return image;
  }
}

function initializeZermielCatalogs(scope = document) {
  scope.querySelectorAll('[data-zermiel-catalog]:not([data-catalog-ready])').forEach((root) => {
    new ZermielPrelaunchCatalog(root);
  });
}

initializeZermielCatalogs();
document.addEventListener('shopify:section:load', (event) => initializeZermielCatalogs(event.target));
